import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { HeygenAvatarConfig } from "@models/avatar-config";
import { RootState } from "@store/store";
import VideoWrap from "../VideoWrap/VideoWrap";
import Loader from "@components/Loader/Loader";
import { useRecordVoice } from "@hooks/useRecordVoice";
import styled from "@emotion/styled";
import { HeygenAvatarService } from "@service/avatar/heygen-avatar.service";
import {
  setInitializing,
  setProcessing,
  setReady,
  setTalking,
  setText,
} from "@store/avatarSlice";
import useStreaming from "@components/HeygenAvatar/useStreaming";
import useBatching from "@components/HeygenAvatar/useBatching";
import { EventType } from "@models/audio-message";

const AvatarContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  position: relative;
  width: 100%;
  height: 100%;
`;

const HeygenAvatar: FC<{
  sessionId: string;
  photoId?: string;
  voiceContextPhotoId?: string;
  gallerySlideEpoch?: number;
  avatarConfig: HeygenAvatarConfig;
  streaming: boolean;
}> = ({
  sessionId,
  photoId,
  voiceContextPhotoId,
  gallerySlideEpoch,
  avatarConfig,
  streaming,
}) => {
  const avatarRef = useRef<HeygenAvatarService | null>(null);
  const dispatch = useDispatch();
  const processing = useSelector((state: RootState) => state.avatar.processing);
  const initialized = useRef(false);
  // eslint-disable-next-line
  const [running, setRunning] = useState(false); // needed to cause component rerender
  const debouncedPhotoIdRef = useRef<string | undefined>();
  const voiceContextPhotoIdRef = useRef<string | undefined>();
  voiceContextPhotoIdRef.current = voiceContextPhotoId ?? photoId;
  const ttsInterruptGenRef = useRef(0);
  const prevGallerySlideEpochRef = useRef<number | undefined>(undefined);

  const {
    sendMessage: sendMessageStreaming,
    interruptPlayback,
    suppressGalleryAssistantStream,
  } = useStreaming({
    sessionId,
    avatar: avatarRef,
  });

  const sendMessageBatching = useBatching({
    sessionId,
    avatar: avatarRef,
    ttsInterruptGenerationRef: ttsInterruptGenRef,
  });

  const sendMessage = useCallback(
    (
      audio?: Blob | ArrayBufferLike,
      photoId?: string,
      eventType?: EventType,
    ) => {
      streaming
        ? sendMessageStreaming(audio as ArrayBufferLike, photoId, eventType)
        : sendMessageBatching(audio as Blob, photoId, eventType);
    },
    [sendMessageBatching, sendMessageStreaming, streaming],
  );

  useRecordVoice(
    processing,
    (audio) =>
      sendMessage(audio, voiceContextPhotoIdRef.current, EventType.UserInput),
    streaming,
  );

  const startAvatar = async (avatar: HeygenAvatarService) => {
    dispatch(setInitializing(true));
    await avatar.start(avatarConfig, (talking) => {
      dispatch(setTalking(talking));
      dispatch(setProcessing(talking));
    });

    if (avatar.running && !avatar.stopping) {
      dispatch(setInitializing(false));
      dispatch(setReady(true));
      sendMessage(undefined, photoId, EventType.NewConversation);
    }
    setRunning(true);
  };

  useEffect(() => {
    if (
      photoId &&
      debouncedPhotoIdRef.current &&
      photoId !== debouncedPhotoIdRef.current
    ) {
      sendMessage(undefined, photoId, EventType.NewPhoto);
    }
    debouncedPhotoIdRef.current = photoId;
  }, [photoId, sendMessage]);

  useEffect(() => {
    if (gallerySlideEpoch === undefined) {
      return;
    }
    if (prevGallerySlideEpochRef.current === undefined) {
      prevGallerySlideEpochRef.current = gallerySlideEpoch;
      return;
    }
    if (prevGallerySlideEpochRef.current === gallerySlideEpoch) {
      return;
    }
    prevGallerySlideEpochRef.current = gallerySlideEpoch;
    ttsInterruptGenRef.current += 1;
    suppressGalleryAssistantStream();
    void interruptPlayback();
  }, [
    gallerySlideEpoch,
    interruptPlayback,
    suppressGalleryAssistantStream,
  ]);

  useEffect(() => {
    if (!initialized.current) {
      dispatch(setProcessing(true));
      initialized.current = true;
      const avatar = new HeygenAvatarService();
      avatarRef.current = avatar;
      startAvatar(avatar);
    }

    return () => {
      if (avatarRef.current?.running && !avatarRef.current.stopping) {
        avatarRef.current.stop();
      }
      dispatch(setReady(false));
      dispatch(setProcessing(false));
    };
  }, [avatarConfig, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!avatarRef.current?.running) {
    return <Loader />;
  }

  return (
    <AvatarContainer>
      <VideoWrap stream={avatarRef.current.stream || undefined} />
    </AvatarContainer>
  );
};

export default memo(HeygenAvatar);
